import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { usePageTitle } from "@/hooks/use-page-title";
import { PlanCalculator } from "@/components/pricing/plan-calculator";
import {
  parseSelection,
  type PlanSelection,
} from "@/components/pricing/plan-selection";
import { PADPULSE_PROMISE } from "@shared/padpulse-promise";
import { MODULAR_TRIAL_DAYS, type AccountKind } from "@shared/modular-pricing";
import { inactiveSubscriptionReason, needsPaymentSetupFromResponse } from "@shared/subscription-gate";

interface MeUser {
  id: number;
  email: string;
  status?: string;
  approved?: boolean;
  hasPaymentMethod?: boolean;
  needsPaymentSetup?: boolean;
  founderStatus?: string | null;
  accountKind?: AccountKind | null;
  subscriptionStatus?: string | null;
  trialEndsAt?: string | null;
}

interface MeResponse {
  user: MeUser;
  activeMembership?: unknown;
  isBillableAccountOwner?: boolean;
  pendingInvitationCount?: number;
}

interface AccountPlanResponse {
  accountKind: AccountKind;
  planTier: "essentials" | "pro";
  modules: string[];
  revenueAddon: boolean;
  researchAddon: boolean;
  cpmListing: boolean;
  roomsOnPadsplit: number;
  roomsOffPadsplit: number;
  portalOwners: number;
  waivedOwners: number;
  offMarketplaceVendors: number;
  hasSubscription: boolean;
  trial: { endsAt: string | null; active: boolean; daysLeft: number };
}

const PENDING_SELECTION_KEY = "padpulse_pending_plan_selection";

/**
 * Onboarding payment step — configure the modular plan (base tier + modules)
 * with the live calculator, then hand off to Stripe hosted checkout, which
 * creates the subscription. This is the ONLY way an owner becomes a paying
 * subscriber: a saved card alone never is, so owners with a card but no live
 * subscription land here too. Checkout honors the account's remaining trial;
 * when the trial is already over the first invoice is charged at checkout.
 */
export default function AppOnboardingPayment() {
  usePageTitle("Choose Your Plan");
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const meQuery = useQuery<MeResponse>({ queryKey: ["/api/app/auth/me"] });
  const planQuery = useQuery<AccountPlanResponse>({
    queryKey: ["/api/app/account/plan"],
    enabled: !!meQuery.data?.user,
    retry: false,
  });

  useEffect(() => {
    if (!meQuery.data) return;
    const u = meQuery.data.user;
    if (!u) {
      navigate("/app/login");
      return;
    }
    if (u.status === "waitlist") {
      navigate("/app/awaiting-approval");
      return;
    }
    // Defense-in-depth (Task #4154): if this page is reached while a pending
    // team invitation still exists, bounce to the accept-invitations screen.
    if ((meQuery.data.pendingInvitationCount ?? 0) > 0) {
      navigate("/app/accept-invitations");
      return;
    }
    // A live subscription (or comped / founder) means nothing to collect. A
    // saved card alone no longer counts — see `needsPaymentSetup`.
    if (!needsPaymentSetupFromResponse(u)) {
      navigate("/app/welcome");
      return;
    }
    // Team members and resident members are never billed directly — only the
    // account owner is.
    const isBillableAccountOwner =
      meQuery.data.isBillableAccountOwner ?? !meQuery.data.activeMembership;
    if (!isBillableAccountOwner) {
      navigate("/app/dashboard");
      return;
    }
  }, [meQuery.data, navigate]);

  // A live subscription means checkout already happened (e.g. webhook landed
  // before the card flag propagated) — nothing to collect here.
  useEffect(() => {
    if (planQuery.data?.hasSubscription) {
      navigate("/app/welcome");
    }
  }, [planQuery.data?.hasSubscription, navigate]);

  const isFounder = meQuery.data?.user?.founderStatus === "active";
  const isComped = meQuery.data?.user?.subscriptionStatus === "comped";

  // Which promise this page can make depends on the account's LOCAL trial
  // clock — the same one the server's Checkout uses (resolveCheckoutTrial):
  //   lapsed     → trial (or a past subscription) is over: charged at checkout
  //   remaining  → no charge today; first charge when the trial ends
  //   fresh      → no trial clock yet: the standard MODULAR_TRIAL_DAYS trial
  const meUser = meQuery.data?.user;
  const lapsedReason = meUser
    ? inactiveSubscriptionReason({
        accountKind: meUser.accountKind ?? null,
        subscriptionStatus: meUser.subscriptionStatus ?? null,
        founderStatus: meUser.founderStatus ?? null,
        trialEndsAt: meUser.trialEndsAt ?? null,
      })
    : null;
  const isLapsed = lapsedReason === "trial_ended" || lapsedReason === "subscription_ended";
  const trialEndDate = meUser?.trialEndsAt ? new Date(meUser.trialEndsAt) : null;
  const hasTrialRemaining = !isLapsed && !!trialEndDate && trialEndDate.getTime() > Date.now();
  const trialEndLabel = trialEndDate
    ? trialEndDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  // Initial calculator state: the pricing-page configuration stashed at
  // signup (if any), overlaid with the server's authoritative account state.
  const initialSelection = useMemo<Partial<PlanSelection>>(() => {
    const stashed = typeof window !== "undefined" ? sessionStorage.getItem(PENDING_SELECTION_KEY) : null;
    const fromStash = stashed ? parseSelection(stashed) : undefined;
    const server = planQuery.data;
    return {
      ...(fromStash ?? {}),
      ...(server
        ? {
            accountKind: server.accountKind,
            ...(fromStash ? {} : {
              planTier: server.planTier,
              modules: server.modules as PlanSelection["modules"],
              revenueAddon: server.revenueAddon,
              researchAddon: server.researchAddon,
              cpmListing: server.cpmListing,
              // No rooms synced yet → seed a sensible starting quote of 10.
              roomsOnPadsplit:
                server.roomsOnPadsplit + server.roomsOffPadsplit > 0 ? server.roomsOnPadsplit : 10,
              roomsOffPadsplit: server.roomsOffPadsplit,
            }),
          }
        : {}),
    };
  }, [planQuery.data]);

  const [checkoutPending, setCheckoutPending] = useState(false);

  // Show a one-shot toast when the user returns here after canceling or
  // letting a Stripe checkout session expire. The cancel_url for onboarding
  // checkout points back to this page with ?checkout=canceled so Stripe
  // redirects them here instead of to billing settings (where a brand-new
  // user has nothing to manage). We use a ref so the toast fires exactly
  // once even if the effect re-runs.
  const canceledToastShown = useRef(false);
  useEffect(() => {
    if (canceledToastShown.current) return;
    const params = new URLSearchParams(window.location.search);
    const checkoutParam = params.get("checkout");
    if (checkoutParam === "canceled") {
      canceledToastShown.current = true;
      toast({
        title: "Checkout session canceled",
        description:
          "Your previous checkout session was canceled or expired. Configure your plan below and try again.",
      });
      // Strip the param from the URL so a refresh or back-nav doesn't
      // re-show the toast. Keep any other params that may be present.
      params.delete("checkout");
      const newSearch = params.toString();
      window.history.replaceState(
        null,
        "",
        window.location.pathname + (newSearch ? `?${newSearch}` : ""),
      );
    }
  }, [toast]);

  const checkoutMutation = useMutation({
    mutationFn: async (selection: PlanSelection) => {
      await apiRequest("PUT", "/api/app/account/plan", {
        planTier: selection.planTier,
        modules: selection.planTier === "essentials" ? selection.modules : [],
        ...(selection.accountKind === "operator"
          ? { revenueAddon: selection.revenueAddon, researchAddon: selection.researchAddon }
          : { cpmListing: selection.cpmListing }),
      });
      // Room estimates seed the FIRST invoice quantities when no rooms are
      // synced yet; live rooms take over (with proration) after PadSplit sync.
      // cancelPath tells the server to send the Stripe cancel_url back here
      // (instead of billing settings) so returning users land on this page
      // and see the "session canceled" toast above.
      const res = await apiRequest("POST", "/api/app/billing/checkout", {
        roomsOnPadsplit: selection.roomsOnPadsplit,
        roomsOffPadsplit: selection.roomsOffPadsplit,
        cancelPath: "/app/onboarding/payment",
      });
      return res.json() as Promise<{ url?: string }>;
    },
    onSuccess: (data) => {
      sessionStorage.removeItem(PENDING_SELECTION_KEY);
      if (data.url) {
        setCheckoutPending(true);
        window.location.href = data.url;
        return;
      }
      navigate("/app/welcome");
    },
    onError: (err: Error) => {
      if (err.message.includes("ALREADY_SUBSCRIBED")) {
        navigate("/app/welcome");
        return;
      }
      toast({
        title: "Could not start checkout",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  // Mirror the redirect guards above: while any of them applies, render only
  // the loader so the calculator never flashes (or lingers) on the way out.
  const me = meQuery.data;
  const redirecting =
    !!me &&
    (!me.user ||
      me.user.status === "waitlist" ||
      (me.pendingInvitationCount ?? 0) > 0 ||
      !needsPaymentSetupFromResponse(me.user) ||
      !(me.isBillableAccountOwner ?? !me.activeMembership) ||
      planQuery.data?.hasSubscription === true);

  if (meQuery.isLoading || redirecting || (meQuery.data?.user && planQuery.isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 flex justify-center">
          <a href="/" data-testid="link-payment-logo">
            <img src={logoImg} alt="PadPulse" className="h-[60px] w-auto cursor-pointer" data-testid="img-payment-logo" />
          </a>
        </div>

        <Card className="w-full p-6 md:p-8 shadow-xl border border-gray-100 bg-white/90 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-5 w-5 text-brand" />
            <h1 className="text-xl font-bold text-gray-900">Build your plan</h1>
          </div>
          <p className="text-sm text-gray-500 mb-5">
            Pick your base plan and modules — you can change any of it later from billing settings.
          </p>

          {isLapsed ? (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-6 space-y-2"
              data-testid="block-trial-ended"
            >
              <p className="text-sm font-semibold text-amber-900" data-testid="text-trial-ended-title">
                {lapsedReason === "subscription_ended"
                  ? "Your subscription has ended — restart it to continue"
                  : "Your trial has ended — add a card to continue"}
              </p>
              <p className="text-sm text-amber-800">
                Your first payment is charged when you complete secure checkout, and your plan unlocks right away.
              </p>
              <p className="text-sm text-amber-800" data-testid="text-trial-ended-promise">{PADPULSE_PROMISE.paidCheckoutLine}</p>
              <p className="text-xs text-amber-700 pt-1">
                Not ready?{" "}
                <Link href="/app/financials" className="font-medium underline" data-testid="link-keep-free-pnl">
                  Keep using the free P&amp;L
                </Link>
              </p>
            </div>
          ) : (
            <div
              className="rounded-lg border border-blue-200 bg-blue-50 p-4 mb-6 space-y-2"
              data-testid="block-payment-reassurance"
            >
              <p className="text-sm font-semibold text-blue-900">You won't be charged today.</p>
              {hasTrialRemaining && trialEndLabel && (
                <p className="text-sm text-blue-800" data-testid="text-first-charge-date">
                  Your free trial continues — your first charge is on {trialEndLabel}, when your trial ends.
                </p>
              )}
              <p className="text-sm text-blue-800" data-testid="text-checkout-promise">
                {hasTrialRemaining ? PADPULSE_PROMISE.paidCheckoutLine : PADPULSE_PROMISE.checkoutLine}
              </p>
              <ul className="text-xs text-blue-700 space-y-1 pt-1">
                <li className="flex items-start gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-blue-500" />
                  {hasTrialRemaining
                    ? "FULL access to everything for the rest of your trial — modules unlock after the trial based on your selection."
                    : `${MODULAR_TRIAL_DAYS}-day free trial with FULL access to everything — modules unlock after the trial based on your selection.`}
                </li>
                <li className="flex items-start gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-blue-500" />
                  Card captured now, first charge only when your trial ends.
                </li>
                <li className="flex items-start gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-blue-500" />
                  Upgrade, downgrade, or cancel anytime.
                </li>
              </ul>
            </div>
          )}

          {isFounder || isComped ? (
            <div className="text-center py-6 space-y-4">
              <p className="text-sm text-gray-600" data-testid="text-founder-plan-note">
                {isFounder
                  ? "As a founding member your plan is comped, so there's nothing to set up here."
                  : "Your plan is comped — there's nothing to set up here."}
              </p>
              <Button
                asChild
                className="bg-brand hover:bg-brand/90 text-white font-semibold"
                data-testid="button-comped-continue"
              >
                <Link href="/app/welcome">Continue to PadPulse</Link>
              </Button>
            </div>
          ) : planQuery.isError ? (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
              data-testid="error-plan-unavailable"
            >
              <p className="font-semibold text-amber-900 mb-1">Plan setup unavailable</p>
              <p>We couldn't load your plan options. Manage your plan from billing settings, or contact support.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                asChild
                data-testid="button-manage-plan-billing"
              >
                <Link href="/app/account/billing">Open billing settings</Link>
              </Button>
            </div>
          ) : (
            <PlanCalculator
              mode="account"
              initialSelection={initialSelection}
              realOwnerData={
                planQuery.data
                  ? { portalOwners: planQuery.data.portalOwners, waivedOwners: planQuery.data.waivedOwners }
                  : undefined
              }
              ctaLabel={
                isLapsed
                  ? "Continue to secure checkout"
                  : hasTrialRemaining
                    ? "Continue your free trial"
                    : `Start your ${MODULAR_TRIAL_DAYS}-day free trial`
              }
              ctaPending={checkoutMutation.isPending || checkoutPending}
              onSubmit={(selection) => checkoutMutation.mutate(selection)}
              footnote={
                <p className="text-xs text-gray-500 text-center" data-testid="text-checkout-footnote">
                  Secure checkout by Stripe. {PADPULSE_PROMISE.receiptFooter}
                </p>
              }
            />
          )}
        </Card>
      </div>
    </div>
  );
}
